import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, RotateCcw, Send, Sparkles } from "lucide-react";
import { practiceApi } from "../../lib/practiceApi";
import "./assistant.css";

const KEY = "ingego-assistant-session";
const rich = text => String(text).split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((part, i) => part.startsWith("**")
  ? <strong key={i}>{part.slice(2, -2)}</strong>
  : <span key={i}>{part}</span>);
const SUGGESTIONS = [
  "Quali documenti servono per una SCIA alternativa al permesso di costruire?",
  "Riassumi gli obblighi del CSE in cantiere",
  "Come organizzo la verifica dell'idoneità tecnico professionale?",
];

export const AssistantWidget = ({ user }) => {
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(() => localStorage.getItem(KEY) || "");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const scroller = useRef(null);

  useEffect(() => {
    if (!session) return;
    practiceApi(`/assistant/history?session_id=${encodeURIComponent(session)}`)
      .then(res => setMessages(res.messages || [])).catch(() => {});
  }, [session]);

  useEffect(() => {
    if (open && scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [messages, open, busy]);

  async function send(question) {
    const message = (question ?? text).trim();
    if (!message || busy) return;
    setText(""); setError(""); setBusy(true);
    setMessages(prev => [...prev, { id: `local-${Date.now()}`, role: "user", content: message }]);
    try {
      const res = await practiceApi("/assistant/chat", { method: "POST", body: JSON.stringify({ session_id: session, message }) });
      if (res.session_id !== session) { setSession(res.session_id); localStorage.setItem(KEY, res.session_id); }
      setMessages(prev => [...prev, { id: `a-${Date.now()}`, role: "assistant", content: res.answer }]);
    } catch (err) {
      setError(err.message);
    } finally { setBusy(false); }
  }

  async function reset() {
    setError("");
    try { if (session) await practiceApi(`/assistant/history?session_id=${encodeURIComponent(session)}`, { method: "DELETE" }); }
    catch (err) { /* la conversazione locale viene comunque azzerata */ }
    localStorage.removeItem(KEY); setSession(""); setMessages([]);
  }

  if (!open) return <button className="assistant-bar" data-testid="assistant-open" onClick={() => setOpen(true)}>
    <span className="assistant-mark"><Sparkles size={15} /></span>
    <span><strong>Assistente INGEGO</strong><small>Chiedi di normativa, cantieri, pratiche</small></span>
  </button>;

  return <aside className="assistant-panel" data-testid="assistant-panel">
    <header className="assistant-head">
      <span className="assistant-mark"><Sparkles size={15} /></span>
      <div><strong>Assistente INGEGO</strong><small>Risposte tecniche in italiano</small></div>
      <button className="assistant-icon" title="Nuova conversazione" data-testid="assistant-reset" onClick={reset}><RotateCcw size={15} /></button>
      <button className="assistant-icon" title="Riduci a barra" data-testid="assistant-collapse" onClick={() => setOpen(false)}><ChevronDown size={17} /></button>
    </header>
    <div className="assistant-body" ref={scroller} data-testid="assistant-messages">
      {!messages.length && <div className="assistant-welcome">
        <p>Ciao {(user?.name || "").split(" ")[0]}, chiedimi di normativa edilizia, sicurezza in cantiere o organizzazione dello studio.</p>
        {SUGGESTIONS.map(s => <button key={s} className="assistant-chip" data-testid={`assistant-suggestion-${SUGGESTIONS.indexOf(s)}`} onClick={() => send(s)}>{s}</button>)}
        <small>L'assistente non consulta internet in tempo reale: verifica sempre le fonti ufficiali.</small>
      </div>}
      {messages.map(m => <div key={m.id || m.created_at} className={`assistant-msg ${m.role}`} data-testid={`assistant-msg-${m.role}`}>{rich(m.content)}</div>)}
      {busy && <div className="assistant-msg assistant typing" data-testid="assistant-typing"><i /><i /><i /></div>}
    </div>
    {error && <p className="assistant-error" role="alert" data-testid="assistant-error">{error}</p>}
    <form className="assistant-form" onSubmit={e => { e.preventDefault(); send(); }}>
      <textarea rows={1} value={text} placeholder="Scrivi la tua domanda…" data-testid="assistant-input"
        onChange={e => setText(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
      <button type="submit" disabled={busy || !text.trim()} data-testid="assistant-send"><Send size={16} /></button>
    </form>
  </aside>;
};
